import { fetchPricesForCurrentTravelData } from "@escapio/portal/api/hotel-prices";
import { createStore } from "@escapio/shared/data-store/DataStore";
import { CHRONOLOGICAL, sorters } from "../hotel-list/Sorting";
import * as HotelPriceLoadingStates from "../../state/HotelPriceLoadingStates";
import { travelData } from "@escapio/portal/state/travelData";
import { removeHotels } from "@escapio/portal/state/FavoriteHotels";
import { trackImpression } from "@escapio/portal/tracker/EventTracker";
import * as Board from "@escapio/portal/components/board";
import { getTitle } from "@escapio/portal/components/favorites/get-title";
import { getUnavailableState } from "@escapio/portal/state/DirectLink";

export const STATE_LOADING = "loading";
export const STATE_LOADED = "loaded";

const DEFAULT_SORTING = CHRONOLOGICAL;

const getBestPrice = pricesForHotel => pricesForHotel.offers[0].total_rate;

const updateDocumentTitle = num => (document.title = getTitle(num));

let favoritesTracked = false;
const trackNumberOfFavorites = num => {
	if (favoritesTracked) {
		return;
	}
	favoritesTracked = true;
	trackImpression("Merkliste", "Anzahl Hotels", num);
};

const ACTION_UPDATE_HOTEL_DATA = (
	state,
	{ unloadedHotelIds, response },
	dispatch,
) => {
	const hotels = { ...state.hotels };
	response.forEach(hotel => {
		hotels[hotel.id] = {
			...hotel,
			state: {
				...state.hotels[hotel.id].state,
				state: STATE_LOADED,
				pricesState: state.hotels[hotel.id].pricesState,
			},
		};
	});

	const offlineHotelIds = unloadedHotelIds.filter(
		id => hotels[id].state.state !== STATE_LOADED,
	);
	offlineHotelIds.forEach(id => delete hotels[id]);

	removeHotels(offlineHotelIds);
	const hotelIds = state.hotelIds.filter(id => !offlineHotelIds.includes(id));

	trackNumberOfFavorites(hotelIds.length);
	updateDocumentTitle(hotelIds.length);

	return ACTION_REQUEST_PRICES(
		{
			...state,
			hotelIds,
			hotels,
		},
		response.map(hotel => hotel.id),
		dispatch,
	);
};

const ACTION_REQUEST_PRICES = (state, payload, dispatch) => {
	if (payload.length == 0) {
		return state;
	}

	const hotels = { ...state.hotels };

	const hotelsLoading = [];
	payload.forEach(id => {
		if (hotels[id].disabled_prices) {
			hotels[id].state.pricesState = getUnavailableState(hotels[id]);
			return;
		}

		if (travelData.hasDates) {
			hotels[id].state.pricesState = HotelPriceLoadingStates.STATE_LOADING;
			hotelsLoading.push(id);
		} else {
			hotels[id].state.pricesState =
				HotelPriceLoadingStates.STATE_WITHOUT_DATES;
		}
	});

	if (hotelsLoading.length > 0) {
		fetchPricesForCurrentTravelData(hotelsLoading, state.filters).then(prices =>
			dispatch(ACTION_UPDATE_PRICES, prices),
		);
	}

	return { ...state, hotels };
};

const ACTION_UPDATE_PRICES = (state, payload) => {
	const hotels = { ...state.hotels };

	let priceFilterRange = state.priceFilterRange;

	Object.keys(payload).forEach(id => {
		if (typeof payload[id] === "object") {
			hotels[id].state = {
				...hotels[id].state,
				...payload[id],
				pricesState: HotelPriceLoadingStates.STATE_AVAILABLE,
			};
			const bestPricePerNight = getBestPrice(payload[id]);
			if (priceFilterRange) {
				priceFilterRange = [
					Math.min(priceFilterRange[0], bestPricePerNight),
					Math.max(priceFilterRange[1], bestPricePerNight),
				];
			} else {
				priceFilterRange = [bestPricePerNight, bestPricePerNight];
			}
		} else {
			hotels[id].state.pricesState = getUnavailableState(hotels[id]);
		}
	});

	return { ...state, hotels, priceFilterRange };
};

export const ACTION_SET_HOTELS = (state, payload, dispatch) => {
	const hotelIds = payload;
	const hotels = Object.assign({}, state.hotels);

	const unloadedHotelIds = hotelIds.filter(id => !hotels.hasOwnProperty(id));

	if (unloadedHotelIds.length === 0) {
		trackNumberOfFavorites(0);
	}

	updateDocumentTitle(hotelIds.length);
	if (unloadedHotelIds.length > 0) {
		const url = new URL(location.origin + "/fetch-hotels/by-ids");

		unloadedHotelIds.forEach(id => {
			hotels[id] = {
				id,
				state: {
					state: STATE_LOADING,
				},
			};
			url.searchParams.append("ids[]", id);
		});

		fetch(url)
			.then(response => response.json())
			.then(response =>
				dispatch(ACTION_UPDATE_HOTEL_DATA, { unloadedHotelIds, response }),
			);
	}

	hotelIds.forEach((id, index) => (hotels[id].state.timeOrder = index));

	return { ...state, hotelIds, hotels };
};

export const ACTION_RESET_PRICES = (state, _, dispatch) => {
	const hotels = state.hotels;

	const reloadHotelsId = state.hotelIds.filter(
		id => hotels[id].state.state === STATE_LOADED,
	);

	return ACTION_REQUEST_PRICES(
		{
			...state,
			hotels: {
				...hotels,
				...reloadHotelsId.reduce(
					(updatedhotels, id) => ({
						...updatedhotels,
						[id]: {
							...hotels[id],
							state: {
								...hotels[id].state,
								pricesState: undefined,
								offers: undefined,
							},
						},
					}),
					{},
				),
			},
		},
		reloadHotelsId,
		dispatch,
	);
};

export const ACTION_SET_SORTING = (state, sorting) => ({
	...state,
	sorting: sorters.hasOwnProperty(sorting) ? sorting : DEFAULT_SORTING,
});

export const ACTION_TOGGLE_CANCELATION_FILTER = (
	state,
	cancelation,
	dispatch,
) =>
	ACTION_RESET_PRICES(
		{
			...state,
			filters: { ...state.filters, cancelation },
		},
		undefined,
		dispatch,
	);

export const ACTION_CHANGE_BOARD_FILTER = (state, board, dispatch) =>
	ACTION_RESET_PRICES(
		{
			...state,
			filters: { ...state.filters, board },
		},
		undefined,
		dispatch,
	);

export const ACTION_SET_PRICE_FILTER = (state, price) => {
	if (
		state.priceFilterRange &&
		(price < state.priceFilterRange[0] || price > state.priceFilterRange[1])
	) {
		return state;
	}
	return { ...state, filters: { ...state.filters, price } };
};
export const ACTION_RESET_PRICE_FILTER = state => {
	return { ...state, filters: { ...state.filters, price: null } };
};

export const store = createStore(
	{
		sorting: DEFAULT_SORTING,
		filters: { board: Board.NONE, cancelation: false },
		hotelIds: undefined,
		hotels: {},
		priceFilterRange: null,
	},
	{
		isLoading: state =>
			state.hotelIds === undefined ||
			state.hotelIds.some(
				id =>
					state.hotels[id].state.state === STATE_LOADING ||
					state.hotels[id].state.pricesState ==
						HotelPriceLoadingStates.STATE_LOADING,
			),
		sortedHotels: state => {
			if (state.hotelIds === undefined) {
				return [];
			}

			const prices = state.hotelIds.reduce(
				(acc, id) => ({ ...acc, [id]: state.hotels[id].state }),
				{},
			);

			return state.hotelIds
				.map(id => state.hotels[id])
				.filter(hotel => hotel.state.state === STATE_LOADED)
				.map(hotel =>
					hotel.state.offers && state.filters.price
						? getBestPrice(hotel.state) <= state.filters.price
							? {
									...hotel,
									state: {
										...hotel.state,
										pricesState: HotelPriceLoadingStates.STATE_AVAILABLE,
									},
								}
							: {
									...hotel,
									state: {
										...hotel.state,
										pricesState: getUnavailableState(hotel),
									},
								}
						: hotel,
				)
				.sort(sorters[state.sorting](prices))
				.map((hotel, index) => ({
					...hotel,
					state: { ...hotel.state, position: index + 1 },
				}));
		},
		priceFiltersVisible: () => travelData.hasDates,
	},
);
